import { trpc } from '../lib/trpc.js';
import { ZoneBadge } from '../components/ui/ZoneBadge.js';
import { StatCard } from '../components/ui/StatCard.js';
import { CapacityBar } from '../components/ui/CapacityBar.js';
import { CplTrendChart } from '../components/charts/CplTrendChart.js';
import { formatGbp, formatPct } from '../lib/format.js';
import { scoreCreative, getCplZone } from '@lcc/shared';
import { clsx } from 'clsx';
import { useState } from 'react';

function CreativeRow({ creative }: { creative: { adName: string; spendGbp: number; leads: number; cpl: number; leadToBookRate: number; revenue: number; daysActive: number; bookings: number; ctr: number } }) {
  const metrics = { ...creative };
  const score = scoreCreative(metrics);
  const zone = creative.cpl > 0 ? getCplZone(creative.cpl) : null;

  return (
    <tr className="border-b border-gray-50 hover:bg-cream-tint transition-colors">
      <td className="py-2.5 px-3 text-sm font-medium text-navy">{creative.adName}</td>
      <td className="py-2.5 px-3 text-sm font-mono">{formatGbp(creative.spendGbp)}</td>
      <td className="py-2.5 px-3 text-sm font-mono">{creative.leads}</td>
      <td className="py-2.5 px-3">
        <span className="font-mono text-sm">{creative.cpl > 0 ? formatGbp(creative.cpl) : '—'}</span>
        {zone && <ZoneBadge zone={zone} size="sm" />}
      </td>
      <td className="py-2.5 px-3 text-sm font-mono">{formatPct(creative.leadToBookRate)}</td>
      <td className="py-2.5 px-3 text-sm font-mono">{formatGbp(creative.revenue)}</td>
      <td className="py-2.5 px-3">
        <span className={clsx(
          'text-xs font-semibold px-2 py-0.5 rounded',
          score.recommendation === 'SCALE' && 'bg-green-100 text-green-800',
          score.recommendation === 'HOLD' && 'bg-blue-50 text-blue-800',
          score.recommendation === 'KILL' && 'bg-red-100 text-red-800',
          score.recommendation === 'INSUFFICIENT_DATA' && 'bg-gray-100 text-gray-600',
        )}>
          {score.recommendation}
        </span>
      </td>
    </tr>
  );
}

function AdvisorPanel() {
  const [brief, setBrief] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const utils = trpc.useUtils();
  const generateBrief = trpc.advisor.generateDailyBrief.useMutation({
    onMutate: () => setIsLoading(true),
    onSuccess: (data) => { setBrief(data.response); setIsLoading(false); },
    onError: () => setIsLoading(false),
  });

  const history = trpc.advisor.history.useQuery({ limit: 3 });

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-navy">AI Advisor</h2>
        <div className="flex gap-2">
          <button
            className="btn-gold text-xs"
            onClick={() => generateBrief.mutate()}
            disabled={isLoading}
          >
            {isLoading ? 'Generating…' : 'Today\'s Brief'}
          </button>
          <a href="/advisor" className="btn-secondary text-xs">Full Advisor</a>
        </div>
      </div>

      {brief && (
        <div className="bg-cream-tint border border-gold/30 rounded-lg p-4 mb-4">
          <p className="text-xs text-gray-500 mb-2 font-medium">TODAY'S BRIEF</p>
          <div className="prose prose-sm max-w-none text-navy whitespace-pre-wrap font-body text-sm leading-relaxed">
            {brief}
          </div>
        </div>
      )}

      {history.data && history.data.length > 0 && !brief && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Recent sessions</p>
          {history.data.map((s) => (
            <a
              key={s.id}
              href={`/advisor/${s.id}`}
              className="block text-sm text-navy hover:text-gold transition-colors border-l-2 border-gray-100 pl-3 py-0.5"
            >
              <span className="capitalize font-medium">{s.type.replace(/_/g, ' ')}</span>
              <span className="text-gray-400 ml-2 text-xs">{new Date(s.createdAt).toLocaleDateString('en-GB')}</span>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

function RampSchedule({ rampDecision }: { rampDecision: { decision: string; newBudget?: number; reason: string } }) {
  const RAMP = [
    { date: '21 Apr', budget: 60, note: 'Today — LCC creative launched' },
    { date: '26 Apr', budget: 75, note: '+25% if CPL ≤£8 for 5 days' },
    { date: '28 Apr', budget: 95, note: '+25% + pause MGGL + 2 new creatives' },
    { date: '5 May', budget: 120, note: 'Launch test campaign' },
    { date: '12 May', budget: 150, note: 'Activate Victor + van 2' },
    { date: '19 May', budget: 180, note: 'Full crew deployed' },
    { date: 'June', budget: 220, note: 'Full pipe ~£25-28k revenue/month' },
  ];

  return (
    <section className="card">
      <h2 className="font-display text-xl text-navy mb-4">Budget Ramp</h2>
      <div className={clsx(
        'rounded-md border px-3 py-2 mb-4 text-sm',
        rampDecision.decision === 'BUMP' && 'bg-green-50 border-green-200 text-green-800',
        rampDecision.decision === 'HOLD' && 'bg-blue-50 border-blue-200 text-blue-800',
        rampDecision.decision === 'CUT' && 'bg-amber-50 border-amber-200 text-amber-800',
        rampDecision.decision === 'HALT' && 'bg-red-50 border-red-200 text-red-800',
      )}>
        <span className="font-semibold">{rampDecision.decision}</span>
        {rampDecision.newBudget && <span className="ml-1">→ £{rampDecision.newBudget}/day</span>}
        <span className="ml-2 text-xs opacity-80">{rampDecision.reason}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <tbody>
            {RAMP.map((step) => (
              <tr key={step.date} className={clsx(
                'border-b border-gray-50',
                step.date === '21 Apr' && 'bg-gold/5 font-medium',
              )}>
                <td className="py-1.5 pr-3 font-mono text-navy whitespace-nowrap">{step.date}</td>
                <td className="py-1.5 pr-3 font-mono">£{step.budget}/day</td>
                <td className="py-1.5 text-gray-500">{step.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function DashboardPage() {
  const { data, isLoading, error } = trpc.metrics.dashboard.useQuery();

  if (isLoading) return <div className="animate-pulse text-gray-400 p-8">Loading dashboard…</div>;
  if (error) return <div className="text-red-600 p-8">Error: {error.message}</div>;
  if (!data) return null;

  const { yesterday, week, cplTrend, rampDecision, health, anomalies } = data;

  return (
    <div className="space-y-6">
      {/* Section 1: Daily Pulse */}
      <section className="card border-l-4 border-l-navy">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Yesterday's Pulse</p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-3xl font-medium text-navy">
                {yesterday.cpl > 0 ? `£${yesterday.cpl.toFixed(2)} CPL` : 'No data'}
              </span>
              <ZoneBadge zone={yesterday.zone} size="lg" />
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {formatGbp(yesterday.spend)} spend · {yesterday.leads} leads
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">
              {health.syncStale
                ? `⚠ HubSpot sync stale (${health.lastSyncMinutesAgo}m ago)`
                : `✓ HubSpot synced ${health.lastSyncMinutesAgo ?? '—'}m ago`}
            </p>
            {health.webhookStale && (
              <p className="text-xs text-amber-600">⚠ Zenbooker webhook silent {health.lastWebhookHoursAgo}h</p>
            )}
          </div>
        </div>
      </section>

      {/* Section 2: Week Tracker */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Leads this week" value={week.leads} />
        <StatCard label="Bookings" value={week.bookings} />
        <StatCard label="Revenue booked" value={formatGbp(week.revenue)} />
        <div className="card">
          <CapacityBar bookings={week.bookings} ceiling={week.capacityCeiling} />
        </div>
      </div>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <section>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Open Alerts</p>
          <div className="space-y-2">
            {anomalies.map((a: { id: string; severity: string; description: string }) => (
              <div
                key={a.id}
                className={clsx(
                  'rounded-md border px-3 py-2 text-sm',
                  a.severity === 'critical' && 'bg-red-50 border-red-200 text-red-800',
                  a.severity === 'high' && 'bg-orange-50 border-orange-200 text-orange-800',
                  a.severity === 'medium' && 'bg-amber-50 border-amber-200 text-amber-800',
                  a.severity === 'low' && 'bg-blue-50 border-blue-200 text-blue-800',
                )}
              >
                {a.description}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Section 3: CPL Trend */}
      <section className="card">
        <h2 className="font-display text-xl text-navy mb-4">30-Day CPL Trend</h2>
        <CplTrendChart data={cplTrend as Array<{ date: string; cpl: number; spend: number; leads: number; zone: string | null }>} />
      </section>

      {/* Section 4: Creative Scorecard */}
      <CreativeScorecard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 5: Advisor */}
        <AdvisorPanel />

        {/* Section 6: Ramp Schedule */}
        <RampSchedule rampDecision={rampDecision} />
      </div>
    </div>
  );
}

function CreativeScorecard() {
  const { data, isLoading } = trpc.creatives.list.useQuery();

  if (isLoading) return <div className="card animate-pulse text-gray-400 h-24" />;
  if (!data || data.length === 0) return (
    <section className="card">
      <h2 className="font-display text-xl text-navy mb-2">Creative Scorecard</h2>
      <p className="text-sm text-gray-500">No creatives yet. Upload a CSV or add a creative to get started.</p>
    </section>
  );

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-navy">Creative Scorecard</h2>
        <a href="/creatives" className="text-xs text-navy hover:text-gold transition-colors">View all →</a>
      </div>
      <div className="overflow-x-auto -mx-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creative</th>
              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leads</th>
              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPL</th>
              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead→Book</th>
              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
              <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signal</th>
            </tr>
          </thead>
          <tbody>
            {data.map((c: { adName: string; metrics14d: { adName: string; spendGbp: number; leads: number; cpl: number; leadToBookRate: number; revenue: number; daysActive: number; bookings: number; ctr: number } }) => (
              <CreativeRow key={c.adName} creative={c.metrics14d} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
